import React, { useCallback, useMemo } from 'react';
import Hls from 'hls.js';
import RendezVousInvestigationDisplay from './RendezVousInvestigationDisplay.jsx';
import ExportPanel from '../../ui/ExportPanel.jsx';
import { rendezVousAdminToBundle } from '../../adapters/rendezVousAdmin.js';
import { mergeAlertIntoRendezVous } from '../../adapters/rendezVousAlertMerge.js';
import { buildRendezVousJournal } from '../../adapters/rendezVousJournal.js';
import { decryptPhoto } from '../../adapters/rendezVousV2.js';
import { downloadDecryptedHls } from '../../export/hlsDecrypt.js';
import { downloadHlsOriginals } from '../../export/hlsOriginals.js';
import { tsToMp4 } from '../../export/mp4Export.js';
import { safeFileName } from '../../export/formats.js';
import { toHex } from '../../crypto/activityCrypto.js';

/** Fiche d'identité du rendez-vous jointe au dossier (rien que des métadonnées). */
function rendezVousSheet(detail, bundle) {
    return {
        identifiant: detail.id,
        titre: detail.title || null,
        statut: detail.status,
        chiffrement: detail.cryptoVersion === 2 ? 'de bout en bout (v2, clé d’enquête)' : 'séquestre serveur (v1)',
        creeLe: detail.createdAt || null,
        prevuLe: detail.scheduledAt || null,
        dureeMinutes: detail.durationMinutes ?? null,
        rencontreConfirmeeLe: detail.foundAt || null,
        termineLe: detail.completedAt || null,
        adresse: bundle?.address || null,
        rayonMetres: detail.radiusMeters ?? null,
        signalePourEnquete: Boolean(detail.flaggedInvestigation),
        examenJusquAu: detail.reviewUntil || null,
        participants: (bundle?.tracks || []).filter(track => track.kind !== 'responder')
            .map(track => ({ role: track.role, nom: track.label, email: track.email || null })),
    };
}

/**
 * Relecture d'un rendez-vous ouvert (dashboard ou lien police) : même écran et
 * même dossier de preuve que la relecture d'une session. L'hôte fournit le
 * détail (déchiffré en v2), les sous-clés, la racine des médias et les en-têtes
 * d'accès (jeton admin ou jeton du lien police).
 *
 */
export default function RendezVousReplay({
    detail, keys = null, apiBaseUrl, mediaBase, headers, evidenceUrl, evidenceHeaders = null, exportsUrl, sourceUrl,
    exportedFrom, header = null, extraToolbar = null, alerts = null,
}) {
    const mediaKey = keys?.mediaHls || null;
    const bundle = useMemo(() => {
        const base = rendezVousAdminToBundle(detail, { apiBaseUrl, mediaBase });
        return mergeAlertIntoRendezVous(base, alerts || []);
    }, [detail, apiBaseUrl, mediaBase, alerts]);
    // Le dossier porte le même journal qu'à l'écran.
    const exportBundle = useMemo(() => (bundle ? { ...bundle, events: buildRendezVousJournal(bundle).map(entry => ({ ...entry, label: entry.text })) } : null), [bundle]);

    const hlsConfig = useMemo(() => {
        const config = { xhrSetup: xhr => Object.entries(headers || {}).forEach(([name, value]) => xhr.setRequestHeader(name, value)) };
        if (!mediaKey) return config;
        // v2 : la clé du flux vient de l'enveloppe ouverte ici, jamais du serveur (key.bin → 410).
        const DefaultLoader = Hls.DefaultConfig.loader;
        class LocalKeyLoader extends DefaultLoader {
            load(context, loaderConfig, callbacks) {
                if (/key\.bin(\?|$)/.test(context.url)) {
                    const data = mediaKey.slice().buffer;
                    callbacks.onSuccess({ url: context.url, data }, { trequest: 0, tfirst: 0, tload: 0, loaded: data.byteLength, total: data.byteLength }, context, null);
                    return;
                }
                super.load(context, loaderConfig, callbacks);
            }
        }
        return { ...config, loader: LocalKeyLoader };
    }, [headers, mediaKey]);

    const fetchBlob = useCallback(async url => {
        const response = await fetch(url, { headers });
        if (!response.ok) throw new Error(`Erreur ${response.status} : contenu indisponible.`);
        const blob = await response.blob();
        // v2 : photo chiffrée par le téléphone.
        if (keys?.events) return new Blob([await decryptPhoto(await blob.arrayBuffer(), keys.events)], { type: 'image/jpeg' });
        return blob;
    }, [headers, keys]);

    // Par flux (participant × vidéo/audio) : l'original déchiffré (TS) et une copie de lecture MP4 redressée.
    const mediaExporters = useMemo(() => (bundle?.tracks || []).flatMap(track => track.media.flatMap(media => {
        const name = media.label || `${media.type === 'video' ? 'Vidéo' : 'Audio'} de ${track.label}`;
        // Un fichier par enregistrement : plusieurs vidéos ou sons successifs ne s'écrasent pas.
        const base = `${safeFileName(track.label)}-${media.alertMedia ? 'alerte-' : ''}${media.type}${media.recordingId ? `-${media.recordingId}` : ''}`;
        const key = media.mediaKey || mediaKey;
        const mediaHeaders = media.mediaHeaders || headers;
        if (!key) return [];
        const decrypt = onProgress => downloadDecryptedHls({ playlistUrl: media.playlistUrl, headers: mediaHeaders, key, onProgress });
        return [
            { id: `${media.id}-ts`, label: `${name} d’origine déchiffrée (TS, sans perte)`, fileName: `${base}.ts`, fetch: decrypt },
            { id: `${media.id}-mp4`, label: `${name} MP4${media.type === 'video' ? ' redressée' : ''} (copie de lecture)`, fileName: `${base}-lecture.mp4`,
                fetch: async onProgress => tsToMp4({ tsBlob: await decrypt(onProgress), onProgress }) },
        ];
    })), [bundle, headers, mediaKey]);

    // Registre, fichiers tels que reçus des téléphones (comparables au registre), fiche et clés.
    const evidence = useCallback(async () => {
        const response = await fetch(evidenceUrl, { headers: evidenceHeaders || headers });
        const registre = response.ok ? await response.json() : null;
        const files = [{ path: 'fiche-rendez-vous.json', content: `${JSON.stringify(rendezVousSheet(detail, bundle), null, 2)}\n`,
            description: 'Fiche du rendez-vous : identifiant, statut, dates, participants, signalement' }];
        for (const track of bundle?.tracks || []) {
            const folder = `medias/originaux/${safeFileName(track.label, `participant-${track.id}`)}`;
            for (const media of track.media) {
                files.push(...await downloadHlsOriginals({ playlistUrl: media.playlistUrl, headers: media.mediaHeaders || headers,
                    folder: `${folder}/${media.alertMedia ? 'alerte-' : ''}${media.type}${media.recordingId ? `-${media.recordingId}` : ''}` }));
            }
            for (const photo of track.photos) {
                const photoResponse = await fetch(photo.url, { headers });
                if (!photoResponse.ok) continue;
                files.push({ path: `${folder}/${photo.purpose === 'arrival' ? 'photo-arrivee' : 'photos'}/${photo.id}-${safeFileName(photo.fileName, 'photo.jpg')}`,
                    content: new Uint8Array(await photoResponse.arrayBuffer()),
                    description: `Photo de ${track.label} telle que reçue de son téléphone${mediaKey ? ' (chiffrée)' : ''}, comparable à l’empreinte du registre` });
            }
        }
        for (const alert of alerts || []) {
            if (!alert.loaded) continue;
            const sessionId = encodeURIComponent(alert.sessionId);
            const proof = await fetch(`${apiBaseUrl.replace(/\/+$/, '')}/admin/sessions/${sessionId}/evidence`,
                { headers: evidenceHeaders || headers });
            if (proof.ok) files.push({ path: `preuve/alertes/${alert.sessionId}/registre.json`,
                content: `${JSON.stringify(await proof.json(), null, 2)}\n`, description: 'Registre de l’alerte liée' });
            const witnesses = await fetch(`${apiBaseUrl.replace(/\/+$/, '')}/admin/rendez-vous/${encodeURIComponent(detail.id)}/alerts/${sessionId}/witnesses`,
                { headers: evidenceHeaders || headers });
            if (witnesses.ok) files.push({ path: `preuve/alertes/${alert.sessionId}/temoins.json`,
                content: `${JSON.stringify(await witnesses.json(), null, 2)}\n`, description: 'Témoins de l’alerte liée' });
        }
        if (keys) {
            files.push({ path: 'preuve/cle-de-lecture.txt',
                content: `${keys.activityKey ? `Clé du rendez-vous : ${toHex(keys.activityKey)}\n` : ''}`
                    + `Clé des événements, positions et photos (AES-GCM, format GCM1) : ${toHex(keys.events)}\n`
                    + `Clé des messages (AES-GCM) : ${toHex(keys.messages)}\n`
                    + `Clé des segments vidéo/audio (AES-128-CBC, HLS) : ${toHex(keys.mediaHls)}\n`
                    + 'Document confidentiel : ces clés permettent de lire le rendez-vous.\n',
                description: 'Clés de lecture (confidentiel)' });
        }
        return {
            registre,
            files,
            context: { exportedFrom, sheet: { type: 'RENDEZ_VOUS', retentionUntil: detail?.reviewUntil || detail?.expiresAt || null } },
        };
    }, [bundle, detail, evidenceUrl, evidenceHeaders, exportedFrom, headers, keys, mediaKey, alerts, apiBaseUrl]);

    // L'empreinte du manifeste est inscrite au journal d'audit avant le téléchargement.
    const recordExport = useCallback(async ({ manifest, manifestSha256 }) => {
        const response = await fetch(exportsUrl, {
            method: 'POST',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ manifestSha256, files: manifest.fichiers.map(file => `${file.sha256}  ${file.octets}  ${file.chemin}`) }),
        });
        if (!response.ok) throw new Error("L'export n'a pas pu être inscrit au journal d'audit.");
    }, [exportsUrl, headers]);

    if (!bundle) return <div className="sid-root sid-message"><p>Rendez-vous illisible.</p></div>;

    return (
        <RendezVousInvestigationDisplay
            key={bundle.id}
            bundle={bundle}
            hlsConfig={hlsConfig}
            fetchBlob={fetchBlob}
            alerts={bundle.alerts}
            header={header}
            toolbar={(
                <>
                    <ExportPanel
                        bundle={exportBundle}
                        fetchBlob={fetchBlob}
                        mediaExporters={mediaExporters}
                        source={sourceUrl}
                        onExported={recordExport}
                        evidence={evidence}
                    />
                    {extraToolbar}
                </>
            )}
        />
    );
}
